/**
 * V46: 승인요청 테스트 데이터 정리 페이지
 *
 * system_admin 전용 관리 페이지
 * - DRY RUN: 삭제 대상 미리보기
 * - 실제 삭제: 모든 승인요청 관련 데이터 삭제
 */

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { Trash2, Search, AlertTriangle, CheckCircle, XCircle, Loader } from 'lucide-react';

interface CollectionSummary {
  collection: string;
  totalCount: number;
  matchedCount: number;
  samples: Array<{
    id: string;
    status?: string;
    type?: string;
    createdAt?: string;
    requesterId?: string;
    companyCode?: string;
  }>;
}

interface CleanupResponse {
  success: boolean;
  message: string;
  isDryRun: boolean;
  collections: CollectionSummary[];
  totalMatchedCount: number;
  totalDeletedCount: number;
  failures: Array<{
    collection: string;
    docId: string;
    error: string;
  }>;
}

export function AdminCleanupPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CleanupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dryRunCompleted, setDryRunCompleted] = useState(false);

  const runCleanup = async (dryRun: boolean) => {
    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const cleanup = httpsCallable<{ dryRun: boolean; includeAllStatuses: boolean }, CleanupResponse>(
        functions,
        'adminCleanupApprovalTestData'
      );

      const response = await cleanup({
        dryRun,
        includeAllStatuses: true,
      });

      setResult(response.data);

      if (dryRun && response.data.success) {
        setDryRunCompleted(true);
      }

      if (!dryRun && response.data.success) {
        setDryRunCompleted(false); // 삭제 후 다시 DRY RUN 필요
      }
    } catch (err: any) {
      console.error('Cleanup error:', err);
      setError(err.message || '알 수 없는 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Trash2 size={24} />
        V46: 승인요청 테스트 데이터 정리
      </h1>
      <p style={{ color: '#666', marginBottom: '24px' }}>
        개발/테스트 중 생성된 승인요청 관련 데이터를 안전하게 삭제합니다.
      </p>

      {/* 경고 박스 */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
      }}>
        <AlertTriangle size={20} style={{ color: '#856404', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <strong style={{ color: '#856404' }}>주의사항</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#856404' }}>
            <li>이 기능은 <code>system_admin</code> 권한이 있는 사용자만 사용할 수 있습니다.</li>
            <li>삭제된 데이터는 복구할 수 없습니다.</li>
            <li>반드시 DRY RUN으로 삭제 대상을 확인한 후 실제 삭제를 진행하세요.</li>
          </ul>
        </div>
      </div>

      {/* 삭제 대상 컬렉션 */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>📋 삭제 대상 컬렉션</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>컬렉션</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>설명</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '8px' }}><code>approvalRequests</code></td>
              <td style={{ padding: '8px' }}>모든 승인 워크플로우 문서</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '8px' }}><code>leaveRequests</code></td>
              <td style={{ padding: '8px' }}>휴가 신청 문서</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '8px' }}><code>attendanceManualRequests</code></td>
              <td style={{ padding: '8px' }}>출퇴근 수기조정 요청</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '8px' }}><code>overtimeRequests</code></td>
              <td style={{ padding: '8px' }}>연장근무 요청</td>
            </tr>
            <tr>
              <td style={{ padding: '8px' }}><code>attendanceAuditLogs</code></td>
              <td style={{ padding: '8px' }}>출퇴근 감사 로그</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 실행 버튼 */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>🚀 실행</h2>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          1️⃣ 먼저 DRY RUN으로 삭제 대상을 확인하세요.<br />
          2️⃣ 확인 후 "실제 삭제" 버튼을 클릭하세요.
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => runCleanup(true)}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              background: isLoading ? '#ccc' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {isLoading ? <Loader size={18} className="animate-spin" /> : <Search size={18} />}
            DRY RUN (미리보기)
          </button>

          <button
            onClick={() => runCleanup(false)}
            disabled={isLoading || !dryRunCompleted}
            style={{
              padding: '12px 24px',
              background: isLoading || !dryRunCompleted ? '#ccc' : '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: isLoading || !dryRunCompleted ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {isLoading ? <Loader size={18} className="animate-spin" /> : <Trash2 size={18} />}
            실제 삭제 (모든 상태)
          </button>
        </div>

        {!dryRunCompleted && !isLoading && (
          <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>
            ⚠️ 실제 삭제를 하려면 먼저 DRY RUN을 실행하세요.
          </p>
        )}
      </div>

      {/* 에러 표시 */}
      {error && (
        <div style={{
          background: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
        }}>
          <XCircle size={20} style={{ color: '#c62828' }} />
          <span style={{ color: '#c62828' }}>{error}</span>
        </div>
      )}

      {/* 결과 표시 */}
      {result && (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {result.success ? (
              <CheckCircle size={20} style={{ color: '#2e7d32' }} />
            ) : (
              <XCircle size={20} style={{ color: '#c62828' }} />
            )}
            {result.isDryRun ? 'DRY RUN 결과' : '삭제 결과'}
          </h2>

          <div style={{
            background: result.success ? '#e8f5e9' : '#ffebee',
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            color: result.success ? '#2e7d32' : '#c62828',
          }}>
            {result.message}
          </div>

          {/* 컬렉션별 결과 */}
          {result.collections.map((col) => (
            <div key={col.collection} style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                {col.collection}
                <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                  (전체: {col.totalCount}건, 대상: {col.matchedCount}건)
                </span>
              </h3>

              {col.samples.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>status</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>type</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>companyCode</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #ddd' }}>createdAt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {col.samples.map((sample) => (
                        <tr key={sample.id}>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd', fontFamily: 'monospace', fontSize: '12px' }}>
                            {sample.id.substring(0, 12)}...
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{sample.status || '-'}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{sample.type || '-'}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{sample.companyCode || '-'}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{sample.createdAt || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          {/* 실패 목록 */}
          {result.failures.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#c62828', marginBottom: '8px' }}>
                삭제 실패 ({result.failures.length}건)
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {result.failures.map((f, i) => (
                  <li key={i} style={{ fontSize: '13px', color: '#c62828' }}>
                    {f.collection}/{f.docId}: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* JSON 원본 */}
          <details style={{ marginTop: '16px' }}>
            <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
              📋 JSON 원본 보기
            </summary>
            <pre style={{
              background: '#263238',
              color: '#aed581',
              padding: '16px',
              borderRadius: '6px',
              overflow: 'auto',
              fontSize: '12px',
              marginTop: '8px',
            }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default AdminCleanupPage;
